import type { Result } from 'true-myth';

import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type {
    PositionSetupAnswerRequest,
    PositionSetupPackage,
    PositionSetupRun,
    PositionSetupRunStartRequest,
} from '../../data-contract/position-setup';
import { positionSetupPackageSchema, positionSetupRunSchema } from '../../data-contract/position-setup';

const RUNS_BASE = '/v1/agent/position-setup/runs';

type Fetched<Value> = Promise<Result<Value, HttpFetcherError>>;

type RequestContext = {
    readonly signal?: AbortSignal;
};

export type PositionSetupRunTarget = { readonly runId: string };

export type FetchLatestPositionSetupRunInput = RequestContext & { readonly architectureId: string };

export type SubmitPositionSetupAnswersInput = PositionSetupRunTarget & {
    readonly answers: readonly PositionSetupAnswerRequest[];
};

export type PositionSetupFetchers = {
    readonly cancelPositionSetupRun: (input: PositionSetupRunTarget) => Fetched<PositionSetupRun>;
    readonly fetchLatestPositionSetupRun: (input: FetchLatestPositionSetupRunInput) => Fetched<PositionSetupRun>;
    readonly fetchPositionSetupPackage: (
        input: RequestContext & PositionSetupRunTarget,
    ) => Fetched<PositionSetupPackage>;
    readonly skipPositionSetupQuestions: (input: PositionSetupRunTarget) => Fetched<PositionSetupRun>;
    readonly startPositionSetupRun: (request: PositionSetupRunStartRequest) => Fetched<PositionSetupRun>;
    readonly submitPositionSetupAnswers: (input: SubmitPositionSetupAnswersInput) => Fetched<PositionSetupRun>;
};

export type PositionSetupFetchersDependencies = {
    readonly validatingRealtimeApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createPositionSetupFetchers = ({
    validatingRealtimeApiFetcher,
}: PositionSetupFetchersDependencies): PositionSetupFetchers => ({
    cancelPositionSetupRun: ({ runId }) =>
        validatingRealtimeApiFetcher.post(positionSetupRunSchema, { pathname: `${RUNS_BASE}/${runId}/cancel` }),
    fetchLatestPositionSetupRun: ({ architectureId, signal }) =>
        validatingRealtimeApiFetcher.get(positionSetupRunSchema, {
            expectedStatusCodes: [404],
            pathname: `${RUNS_BASE}/latest`,
            requestOptions: { searchParams: { positionArchitectureId: architectureId }, signal },
        }),
    fetchPositionSetupPackage: ({ runId, signal }) =>
        validatingRealtimeApiFetcher.get(positionSetupPackageSchema, {
            pathname: `${RUNS_BASE}/${runId}/package`,
            requestOptions: { signal },
        }),
    skipPositionSetupQuestions: ({ runId }) =>
        validatingRealtimeApiFetcher.post(positionSetupRunSchema, { pathname: `${RUNS_BASE}/${runId}/skip` }),
    startPositionSetupRun: (request) =>
        validatingRealtimeApiFetcher.postJson(positionSetupRunSchema, { pathname: RUNS_BASE, payload: request }),
    submitPositionSetupAnswers: ({ answers, runId }) =>
        validatingRealtimeApiFetcher.postJson(positionSetupRunSchema, {
            pathname: `${RUNS_BASE}/${runId}/answers`,
            payload: { answers },
        }),
});
